import { useCallback, useRef } from 'react';
import { Vendor } from '../types/types';
import { APP_CONFIG } from '../lib/appConstants';
import { getBatchVendorLocations } from '../lib/vendorApi';
import { logger } from '../lib/logger';

export const useGPSUpdates = (
  vendors: Vendor[],
  visibleVendorIds: number[],
  selectedVendorId: number | null,
  onLocationUpdate: (vendorId: number, location: { latitude: number; longitude: number }) => void
) => {
  const lastUpdateTimeRef = useRef<number>(0);

  const updateGPSLocations = useCallback(async () => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

    if (timeSinceLastUpdate < APP_CONFIG.GPS_RATE_LIMIT && lastUpdateTimeRef.current > 0) {
      logger.log(
        `Skipping update - last update was ${Math.round(timeSinceLastUpdate / 1000)}s ago`
      );
      return;
    }

    lastUpdateTimeRef.current = now;

    // Find visible GPS vendors
    const visibleGpsVendorIds = vendors
      .filter(vendor => vendor.uses_gps && visibleVendorIds.includes(vendor.id))
      .map(vendor => vendor.id);

    // Add selected vendor if it uses GPS
    if (selectedVendorId) {
      const selectedVendor = vendors.find(v => v.id === selectedVendorId);
      if (selectedVendor?.uses_gps && !visibleGpsVendorIds.includes(selectedVendorId)) {
        visibleGpsVendorIds.push(selectedVendorId);
      }
    }

    if (visibleGpsVendorIds.length === 0) return;

    try {
      const locations = await getBatchVendorLocations(visibleGpsVendorIds);
      Object.entries(locations).forEach(([id, location]) => {
        onLocationUpdate(Number(id), location);
      });
    } catch (error) {
      logger.error('Error fetching batch locations:', error);
    }
  }, [vendors, visibleVendorIds, selectedVendorId, onLocationUpdate]);

  return { updateGPSLocations };
};
